import { RetailerWrapper } from "../styles";
import { Button } from "@/common/components/button";
import {
  getUserManagementTableData,
  userManagementTableHeaders,
  userManagementTableKeys,
  getRoleManagementTableData,
  roleManagementTableHeaders,
  roleManagementTableKeys,
  invitationForm,
} from "./data";
import Table from "@/common/components/Table";
import { Pagination } from "@/common/components/pagination";
import { KeyValueObj } from "@/common/types";
import { useMemo, useState } from "react";
import useQuery from "@/hooks/useQuery";
import {
  DRF_ACTIVATE_USER_URL,
  DRF_DEACTIVATE_USER_URL,
  DRF_ROLES,
  DRF_USERS,
  DRT_USERS_COUNT,
  INVITE_DRF_URL,
} from "@/common/constants";
import { Modal } from "@/common/components/modal";
import Form from "@/common/components/form/Form";
import { Prompt } from "@/common/components/prompt";
import Tab2 from "@/common/components/Tab/Tab2";
import { userManagementCards } from "./data";
import { LargeCard } from "../../stats/subs/LargeCard";
import { useNotification } from "@/common/components/Notifications/NotificationProvider";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { EUserStatus } from "@/types/enum";

enum ETabs {
  USER = "User",
  ROLE = "Role",
}

enum EToggleUserStatus {
  ACTIVATE = "activate",
  DEACTIVATE = "deactivate",
}

enum EModal {
  INVITE = "invite",
  REDIRECT = "redirect",
  ACTIVATE = "activate",
  DEACTIVATE = "deactivate",
}
interface IFieldValue {
  value: string;
  type: string;
}
interface IUserTableData {
  id: string;
  email: IFieldValue;
  firstName: IFieldValue;
  lastName: IFieldValue;
  phone: IFieldValue;
  role: IFieldValue;
  status: IFieldValue;
}

const DRFUser = () => {
  const [currentRow, setCurrentRow] = useState<IUserTableData | null>(null);

  const [activeModal, setActiveModal] = useState<EModal | null>(null);

  // Separate pagination state for each tab
  const [userPage, setUserPage] = useState(1);
  const [userPerpage, setUserPerpage] = useState(20);
  const [rolePage, setRolePage] = useState(1);
  const [rolePerpage, setRolePerpage] = useState(20);

  const [currentTab, setCurrentTab] = useState(ETabs.USER);
  const [submitLoading, setSubmitLoading] = useState(false);

  const makeRequest = useApiCallHandling();
  const notify = useNotification();

  const {
    data: users,
    loading: usersLoading,
    meta: usersMeta,
    refetch: refetchUsers,
  } = useQuery(
    DRF_USERS + `?page=${userPage}&per_page=${userPerpage}`,
    currentTab === ETabs.USER
  );

  const { data: roles, loading: rolesLoading } = useQuery(
    DRF_ROLES + `?page=${rolePage}&per_page=${rolePerpage}`
  );

  const { data: usersCount } = useQuery(DRT_USERS_COUNT);

  const handleDropdownClick = (row: IUserTableData) => {
    if (row.id === currentRow?.id) {
      setCurrentRow(null);
    } else {
      setCurrentRow(row);
    }
  };

  const userDropdowns = useMemo(
    () =>
      currentRow?.status?.value === EUserStatus.DEACTIVATED
        ? [
            {
              label: "Activate",
              action: () => {
                setActiveModal(EModal.ACTIVATE);
              },
            },
          ]
        : [
            {
              label: "Deactivate",
              action: () => {
                setActiveModal(EModal.DEACTIVATE);
              },
            },
          ],
    [currentRow]
  );

  const handleTabClick = (tab: string) => {
    setCurrentTab(tab as ETabs);
  };

  const roleDropdowns = useMemo(
    () =>
      currentRow?.role?.value !== "--"
        ? [{ label: "View", action: () => {} }]
        : [{ label: "View", action: () => {} }],
    [currentRow?.role?.value]
  );

  const handleInviteSubmit = (formValues: any) => {
    setSubmitLoading(true);

    makeRequest(
      INVITE_DRF_URL,
      "POST",
      formValues,
      (data) => {
        notify({
          message: data.message || "User invited successfully",
          type: "SUCCESS",
        });
        setActiveModal(null);
      },
      (error) => {
        notify({
          message: error.response?.data?.message || "Error inviting user",
          type: "ERROR",
        });
      },
      () => {
        setSubmitLoading(false);
        refetchUsers();
      },
      { dontNotifyOnSucess: true, dontNotifyonFail: true }
    );
  };

  const tabs = [
    {
      title: ETabs.USER,
      component: (
        <div className="table-wrapper">
          <Table
            tableHeaders={userManagementTableHeaders}
            tableKeys={userManagementTableKeys}
            tableData={getUserManagementTableData(users)}
            tableTitle="List of all users"
            tableSubtitle={(usersMeta?.total_count ?? "0") + " users"}
            loading={usersLoading}
            handleDropDownClicked={(row: KeyValueObj) =>
              handleDropdownClick(row as unknown as IUserTableData)
            }
            showDropDown={Boolean(currentRow)}
            dropDownOptions={userDropdowns}
            spaced
            rowLineColor="light"
          />

          <Pagination
            pageCount={usersMeta?.total_pages || 1}
            onNextPage={(event) => setUserPage(event.selected)}
            onPerPageChange={(event) =>
              setUserPerpage(Number(event?.target?.value))
            }
          />
        </div>
      ),
    },
    {
      title: ETabs.ROLE,
      component: (
        <div className="table-wrapper">
          <Table
            tableHeaders={roleManagementTableHeaders}
            tableKeys={roleManagementTableKeys}
            tableData={getRoleManagementTableData(roles)}
            tableTitle="Role allocation history"
            loading={rolesLoading}
            handleDropDownClicked={(row: KeyValueObj) =>
              handleDropdownClick(row as any)
            }
            showDropDown={Boolean(currentRow)}
            dropDownOptions={roleDropdowns}
            spaced
            rowLineColor="light"
          />

          <Pagination
            pageCount={roles?.meta?.total_pages || 1}
            onNextPage={(event: any) => setRolePage(event.selected)}
            onPerPageChange={(event: any) =>
              setRolePerpage(Number(event?.target?.value))
            }
          />
        </div>
      ),
    },
  ];

  const toggleUserStatus = (user_id: string, toggleTo: EToggleUserStatus) => {
    setSubmitLoading(true);

    toggleTo === EToggleUserStatus.DEACTIVATE
      ? makeRequest(
          DRF_DEACTIVATE_USER_URL,
          "POST",
          { user_id },
          () => {},
          () => {},
          () => {
            setSubmitLoading(false);
            setActiveModal(null);
            setCurrentRow(null);
            refetchUsers();
          }
        )
      : makeRequest(
          DRF_ACTIVATE_USER_URL,
          "POST",
          { user_id },
          () => {},
          () => {},
          () => {
            setSubmitLoading(false);
            setActiveModal(null);
            setCurrentRow(null);
            refetchUsers();
          }
        );
  };

  return (
    <RetailerWrapper>
      {/* card wrapper */}
      <div className="card-wrapper">
        {userManagementCards(usersCount)?.map((card: any) => (
          <LargeCard
            color={card?.color}
            icon={card.icon}
            value={card.value}
            title={card.name}
          />
        ))}
      </div>

      <div>
        <div className="top-wrapper">
          <Button onClick={() => setActiveModal(EModal.INVITE)}>
            Invite New User
          </Button>
        </div>
      </div>

      <Modal
        title="User Invitation"
        isOpen={activeModal === EModal.INVITE}
        close={() => setActiveModal(null)}
      >
        <div className="modal max-w-[600px] md:w-[600px]">
          <Form
            formInputs={invitationForm(roles)}
            processInputs={handleInviteSubmit}
            loadingState={submitLoading}
          />
        </div>
      </Modal>

      <Tab2
        onTabClick={handleTabClick}
        currentTab={currentTab}
        tabs={tabs}
        key={currentTab}
      />

      <Modal
        isOpen={
          activeModal === EModal.ACTIVATE || activeModal === EModal.DEACTIVATE
        }
        close={() => setActiveModal(null)}
      >
        <Prompt
          prompt={`Are you sure you want to ${
            activeModal === EModal.DEACTIVATE ? "deactivate" : "activate"
          } user`}
          type={activeModal === EModal.DEACTIVATE ? "delete" : "accept"}
          onMainAction={(_?: any) => {
            toggleUserStatus(
              currentRow?.id as string,
              // @ts-ignore
              activeModal
            );
          }}
          onSecondaryAction={() => {
            setCurrentRow(null);
            setActiveModal(null);
          }}
          mainLoading={submitLoading}
        />
      </Modal>
    </RetailerWrapper>
  );
};

export default DRFUser;
